/**
 * AWS Cognito Authentication Configuration
 *
 * This configuration is used by react-oidc-context to authenticate users
 * with AWS Cognito User Pool.
 */

// Get the base URL for redirect URIs
const getBaseUrl = () => {
  // In production, use the actual domain from env or default
  if (process.env.NODE_ENV === "production") {
    return process.env.REACT_APP_BASE_URL || "https://sandbox.avipair.com";
  }
  // In development, use current origin
  return window.location.origin;
};

export const cognitoAuthConfig = {
  // Cognito User Pool authority endpoint
  authority:
    process.env.REACT_APP_COGNITO_AUTHORITY ||
    `https://cognito-idp.${
      process.env.REACT_APP_COGNITO_REGION || "us-east-1"
    }.amazonaws.com/${process.env.REACT_APP_COGNITO_USER_POOL_ID || ""}`,

  // App Client ID from Cognito
  client_id: process.env.REACT_APP_COGNITO_CLIENT_ID || "",

  // Redirect URI after successful authentication
  redirect_uri: `${getBaseUrl()}/authentication/login`,

  // OAuth 2.0 response type (Authorization Code Flow)
  response_type: "code",

  // Scopes requested from Cognito
  scope: "aws.cognito.signin.user.admin email openid phone profile",

  // Redirect URI after logout
  post_logout_redirect_uri: `${getBaseUrl()}/authentication/login`,

  // Automatically renew tokens before expiration
  automaticSilentRenew: true,

  // Load user info after authentication
  loadUserInfo: true,

  // Silent renew redirect URI (for token refresh)
  silent_redirect_uri: `${getBaseUrl()}/authentication/silent-callback`,

  // Additional settings
  revokeAccessTokenOnSignout: true,
};

/**
 * Get Cognito logout URL
 *
 * Note: You need to replace <your-cognito-domain> with your actual Cognito domain.
 * You can find this in AWS Cognito Console > App integration > Domain
 *
 * Example: If your domain is "avipair.auth.us-east-1.amazoncognito.com",
 * then use: "https://avipair.auth.us-east-1.amazoncognito.com"
 *
 * For full logout (clears Cognito session), use this URL.
 * For local logout only, use auth.removeUser() instead.
 */
export const getCognitoLogoutUrl = (cognitoDomain) => {
  if (!cognitoDomain) {
    console.warn("Cognito domain not provided. Using local logout only.");
    return null;
  }

  const clientId = cognitoAuthConfig.client_id;
  const logoutUri = cognitoAuthConfig.post_logout_redirect_uri;

  return `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
    logoutUri
  )}`;
};

/**
 * Extract user role from Cognito token
 * Supports both Cognito Groups and custom attributes
 */
export const getUserRole = (user) => {
  if (!user) {
    return "user";
  }

  // Handle both old format (user.profile) and new format (user directly)
  const profile = user.profile || user;

  // Method 1: Check custom:role attribute
  if (profile["custom:role"]) {
    return profile["custom:role"]?.toLowerCase();
  }

  // Method 2: Check cognito:groups (first group is primary role)
  if (profile["cognito:groups"] && profile["cognito:groups"].length > 0) {
    return profile["cognito:groups"][0]?.toLowerCase();
  }

  // Method 3: Fallback to user
  return "user";
};

/**
 * Get all user groups/roles
 */
export const getUserGroups = (user) => {
  if (!user) {
    return [];
  }

  const profile = user.profile || user;
  return profile["cognito:groups"] || [];
};

/**
 * Check if user has a specific role
 */
export const hasRole = (user, role) => {
  const userRole = getUserRole(user);
  const userGroups = getUserGroups(user);

  // Admin has access to everything
  if (userRole === "admin" || userGroups.includes("admin")) {
    return true;
  }

  return userRole === role || userGroups.includes(role);
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (user, roles) => {
  return roles.some((role) => hasRole(user, role));
};

/**
 * Get user's profileId from Cognito attributes
 */
export const getUserProfileId = (user) => {
  if (!user) {
    return null;
  }

  const profile = user.profile || user;
  
  // Try custom:profileId first (from Cognito attributes)
  if (profile["custom:profileId"]) {
    return profile["custom:profileId"];
  }
  
  // Fallback to profileId if it exists
  if (profile.profileId) {
    return profile.profileId;
  }

  return null;
};
